import React, { useEffect, useState } from 'react';
import './Login.css';
import { persistAuthSession } from '../utils/auth';

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_URL || '';
  const microsoftStatusUrl = `${apiBaseUrl}/auth/microsoft/status`;
  const microsoftLoginUrl = `${apiBaseUrl}/auth/microsoft/login`;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const authError = params.get('auth_error');
    if (authError) {
      setError(authError);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const loadMicrosoftStatus = async () => {
      try {
        const response = await fetch(microsoftStatusUrl);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        setMicrosoftEnabled(Boolean(data.enabled));
      } catch (err) {
        console.warn('Failed to load Microsoft auth status:', err);
      }
    };

    loadMicrosoftStatus();
  }, [microsoftStatusUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (data.status === 'success' && data.session_token) {
        persistAuthSession(data.session_token, data.user_info);
        
        // Call success callback
        onLoginSuccess(data.user_info, data.session_token);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleMicrosoftLogin = () => {
    window.location.assign(microsoftLoginUrl);
  };

  return (
    <div className="login-container">
      <div className="login-modal">
        {microsoftEnabled && (
          <>
            <button
              type="button"
              className="microsoft-login-button"
              onClick={handleMicrosoftLogin}
              disabled={loading}
            >
              Sign in with Microsoft
            </button>
            <div className="login-divider">
              <span>or</span>
            </div>
          </>
        )}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter your username"
              disabled={loading}
              autoComplete="username"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter your password"
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-button"
            disabled={loading || !formData.username || !formData.password}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
